<template>
    <div class="mx-3">
        <service-list-filters-modal></service-list-filters-modal>
        <document-tags></document-tags>
        <document-notes-modal></document-notes-modal>
        <link-payer-authorization-modal></link-payer-authorization-modal>        

        <div v-if="clients.length === 1" class="h4">
            {{ clientName }}
        </div>
        <div v-else class="dropdown h4">
            <a id="clientDropdown" class="dropdown-toggle" href="#" role="button" data-toggle="dropdown">
                {{ clientName }}
            </a>
            <div class="dropdown-menu dropdown-menu-right">
                <button :key="'client-dropdown-menu-item-0'" class="h5 dropdown-item" @click="changeClient(0)">All Clients</button>
                <button v-for="client in clients" :key="'client-dropdown-menu-item-' + client.id" class="h5 dropdown-item" @click="changeClient(client.id)">{{ client.name }}</button>
            </div>
        </div>
        <div class="d-flex justify-content-between align-items-end">
            <div>
                <h2>
                    Health Insurance Claim Forms
                    <span class="h2-italic-sm text-muted">(CMS 1500/1450)</span>
                </h2>
            </div>
            <div class="btn-toolbar mb-2" role="toolbar">
                <div class="input-group mr-2">
                    <div class="input-group-prepend">
                        <button class="btn dropdown-toggle input-group-text" type="button" data-toggle="dropdown">{{ searchOption.name }}</button>
                        <div class="dropdown-menu">
                            <button v-for="option in searchOptions" :key="'search-option-' + option.id" class="dropdown-item text-primary" @click="setSearchOption(option)">{{ option.name }}</button>
                        </div>
                    </div>                    
                    <input ref="searchInput" :type="searchOption.type" class="form-control" :placeholder="searchOption.placeholder" v-model="searchString" @keyup.enter="search()">
                    <div class="input-group-append">
                        <button class="btn btn-primary" @click="search()"><i class="fas fa-search fa-lg"></i></button>
                    </div>                    
                </div>
                <div class="btn-group">
                    <button class="btn btn-primary" data-toggle="tooltip" title="Filters" @click="showFilters()">
                        <i class="fas fa-filter fa-lg"></i>                   
                    </button>
                </div>                
            </div>
        </div>        
        <div v-if="services.length" class="table-responsive">
            <table class="table table-striped table-sm">
                <thead>
                    <tr>
                        <th></th>
                        <th>Patient Name</th>
                        <th>PAN</th>
                        <th>DOS</th>
                        <!-- <th>Transaction Code</th> -->
                        <th>Payer</th>
                        <th>Rendering Provider</th>
                        <th>Submitted</th>
                        <th class="text-right">Est. Reimbursement</th>
                        <th class="text-right">Paid Amount</th>
                        <th class="text-right">Balance</th>
                        <th class="text-right">Days Outstanding</th>
                        <th class="fit text-center">Remit</th>
                        <th class="fit text-center">Auth</th>
                        <th class="fit text-center">1008</th>
                        <th>Tags</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="service in services" :key="'service-list-' + service.id">
                        <td class="fit">
                            <div class="d-flex align-items-center">
                                <div>
                                    <i :class="['fas fa-sm fa-fw text-muted mx-1 align-middle mb-1', {'fa-balance-scale': service.included_in_1008}]" :title="service.included_in_1008 ? 'Associated with 1008' : false"></i>
                                </div>
                                <div>
                                    <document-status-icon :document-id="service.document_id" :document-status="service.document_status"></document-status-icon>
                                </div>
                            </div>                        
                        </td>
                        <td><a :href="getClaimUrl(service)">{{ service.patient_name }}</a></td>
                        <td><a :href="getUrl(service)">{{ service.pan }}</a></td>
                        <td>{{ service.dos }}</td>
                        <!-- <td>{{ service.transaction_code }}</td> -->
                        <td>{{ service.payer_name }}</td>
                        <td>{{ service.rendering_provider_name }}</td>
                        <td>{{ service.submitted_at | localizeTime('YYYY-MM-DD HH:mm:ss')}}</td>
                        <td class="text-right">{{ service.estimated_reimbursement | currency }}<sup v-if="service.has_contract">*</sup></td>
                        <td class="text-right">{{ service.paid_amount | currency }}</td>
                        <td class="text-right">{{ service.balance | currency }}</td>
                        <td class="text-right">{{ service.days_outstanding }}</td>
                        <td class="fit text-center">
                            <span v-if="service.has_remit" class="indicator text-success">&#x2713;</span>
                            <span v-else class="indicator text-danger">&#x2717;</span>
                        </td>
                        <td class="fit text-center">
                            <span v-if="service.has_associated_authorization" class="indicator text-success">&#x2713;</span>
                            <span v-else class="indicator text-danger">&#x2717;</span>
                        </td>
                        <td class="fit text-center">
                            <span v-if="service.included_in_1008" class="indicator text-success">&#x2713;</span>
                            <span v-else class="indicator text-danger">&#x2717;</span>
                        </td>                        
                        <td class="fit">
                            <document-tags-button :document="service"></document-tags-button>
                        </td>
                        <td class="fit p-0">
                            <div class="d-flex flex-nowrap justify-content-end align-items-center">

                                <!-- fill empty space -->
                                <div class="flex-fill"></div>

                                <!-- view cms document -->
                                <div class="mx-1">
                                    <a class="btn btn-link p-0" :href="getDocumentUrl(service)" target="_blank" title="View CMS Form">
                                        <i class="fas fa-file-pdf fa-lg fa-fw"></i>
                                    </a>
                                </div> 

                                <!-- associate payer authorization -->
                                <div class="mx-1">
                                    <button class="btn btn-sm btn-link p-0" title="Link Payer Authorization to Claim" @click="showPayerAuthorization(service)">
                                        <i :class="['fas fa-thumbs-up fa-lg', { 'text-success': service.has_associated_authorization }]"></i>
                                    </button>
                                </div>                                                               

                                <!-- document notes -->
                                <div class="mx-1">
                                    <button class="btn btn-link p-0" title="Document Notes" @click="onDocumentNotes(service)">
                                        <i class="fas fa-comment fa-lg fa-fw"></i>
                                    </button>
                                </div>                                

                                <!-- share with attorney(s) -->                                
                                <div class="mx-1">                
                                    <share-button v-if="canShare" :shared="service.is_shared" :claim-id="service.claim_id" @click="toggleSharing" :disabled="service.disable_sharing === 1"></share-button>
                                    <i v-else class="fas fa-lg fa-fw"></i>
                                </div>
                            </div>                                                    
                        </td>                      
                    </tr>
                </tbody>
            </table>
            <div v-if="showFootnote" class="font-italic text-muted">
                * <small>{{ ContractDisclaimer }}</small>
            </div>            

            <div class="d-flex">
                <nav v-if="pageCount > 1" aria-label="Page navigation">
                    <ul class="pagination">
                        <li v-bind:class="['page-item', { 'disabled' : currentPage == 1 }]">
                            <a class="page-link" href="#" @click="first()">
                                <span id="first-page"><i class="fas fa-step-backward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                        
                        <li v-bind:class="['page-item', { 'disabled' : currentPage == 1 }]">
                            <a class="page-link" href="#" @click="previous()">
                                <span id="previous"><i class="fas fa-chevron-left fa-xs chev-pos"></i><i class="fas fa-chevron-left fa-xs mr-1 chev-pos"></i>Previous</span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : currentPage == pageCount }]">
                            <a class="page-link" href="#" @click="next()">
                                <span id="next">Next<i class="fas fa-chevron-right fa-xs ml-2 chev-pos"></i><i class="fas fa-chevron-right fa-xs chev-pos"></i></span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : currentPage == pageCount }]">
                            <a class="page-link" href="#" @click="last()">
                                <span id="first-page"><i class="fas fa-step-forward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                        
                    </ul>
                </nav>
                <div class="ml-auto"> Page {{ currentPage }} of {{ pageCount }}</div>
            </div>            
        </div>
        <div v-else-if="isLoading" class="pt-3 border-top">            
            <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
        </div>
        <div v-else class="pt-3 border-top">
            <h3 class="text-muted text-center">No Active CMS 1500/1450 Documents</h3>
        </div>                 
    </div>    
</template>

<script>
import { ContractDisclaimer } from '../../constants.js';
import ServiceListFiltersModal from './ServiceListFiltersComponent.vue';
import DocumentStatusIcon from '../documents/DocumentStatusIconComponent.vue';
import DocumentTags from '../documents/DocumentTagsComponent.vue';
import DocumentNotesModal from '../documents/DocumentNotesModalComponent.vue';
import DocumentTagsButton from '../buttons/DocumentTagsButtonComponent.vue';
import ShareButton from '../buttons/ShareButtonComponent.vue';
import LinkPayerAuthorizationModal from '../LinkPayerAuthorizationComponent.vue';

export default {
    components: {
        ServiceListFiltersModal,
        DocumentStatusIcon,
        DocumentTags,
        DocumentNotesModal,
        DocumentTagsButton,
        ShareButton,
        LinkPayerAuthorizationModal
    },
    props: {
        clientIdProperty: Number,
        clients: Array,
        filtersProperty: Object,
        pageProperty: Number,
        searchOptions: Array,
        searchOptionId: Number,
        searchStringProperty: String,
        canShare: Boolean,
        isResearcher: Boolean
    },
    data() {
        return {
            ContractDisclaimer: ContractDisclaimer,
            
            clientId: 0,
            services: [],
            filters: this.filtersProperty,
            currentPage: this.pageProperty,
            pageCount: 0,
            isLoading: false,
            searchOption: this.searchOptions.find(x => x.id === this.searchOptionId),
            searchString: this.searchStringProperty,
            channels: []
        }
    },
    computed: {
        clientName: function () {
            return this.clientId ? this.clients.find(x => x.id == this.clientId).name : 'All Clients';
        },
        showFootnote: function () {
            return this.services.some((document) => document.has_contract);
        }     
    },
    methods: {
        refresh: function () {

            this.isLoading = true;
            axios.get('/ajax/clients/' + this.clientId + '/services', {
                params: {
                    filters: this.filters,
                    search_option_id: this.searchOption.id,
                    search_string: this.searchString,                    
                    page: this.currentPage
                }
            })
            .then(response => {
                this.services = response.data.services;
                this.pageCount = response.data.page_count;
                this.isLoading = false;
            })
            .catch(error => {
                console.log('got an error...');
                this.isLoading = false;
            });            
        },
        changeClient: function (id) {
            this.clientId = id;
            this.currentPage = 1;
            this.refresh();
            this.listen();            
        },
        first: function () {
            if (this.currentPage > 1) {
                this.currentPage = 1;
                this.refresh();
            }
        },        
        previous: function() {
            if (this.currentPage > 0) {
                this.currentPage--;
                this.refresh();
            }
        },
        next: function() {
            if (this.currentPage < this.pageCount) {
                this.currentPage++;
                this.refresh();
            }
        },
        last: function () {
            if (this.currentPage < this.pageCount) {
                this.currentPage = this.pageCount;
                this.refresh();
            }
        },
        setSearchOption: function (option) {
            this.searchOption = option;
        },
        search: function () {
            this.currentPage = 1;
            this.refresh();
        },
        showFilters: function () {
            eventHub.$emit('show-service-list-filters', this.filters, this.filter);
        },
        filter: function (filters) {
            this.filters = filters;
            this.refresh();
        },
        toggleSharing: function (shared, claimId) {

            let url = `/ajax/shared-claims/${claimId}`;
            let method = shared ? 'delete' : 'post';

            axios({ method, url })
                .then(response => {
                    toastr[response.data.level](response.data.message, response.data.title, { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                    this.refresh();                               
                })
                .catch(error => {
                    toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                });         
        },
        getClaimUrl: function (service) {

            let subDir = `/claims/${service.claim_id}`;
            if (this.isResearcher) {
                subDir = `/researcher/clients/${service.client_id}/claims/${service.claim_id}`;
            }
            else if (!this.canShare) {
                subDir = `/attorney/claims/${service.claim_id}`;
            }
            
            let href = window.location.origin + subDir;

            let url = new URL(href);
            url.searchParams.append('clientId', service.client_id);
            url.searchParams.append('searchOptionId', this.searchOption.id);
            url.searchParams.append('searchString', this.searchString);
            url.searchParams.append('filters', JSON.stringify(this.filters));
            url.searchParams.append('page', this.currentPage);

            return url.href;
        },
        getUrl: function (service) {

            let href = window.location.origin + `/claims/${service.claim_id}/documents/${service.document_id}/services`;

            let url = new URL(href);
            url.searchParams.append('clientId', service.client_id);
            url.searchParams.append('searchOptionId', this.searchOption.id);
            url.searchParams.append('searchString', this.searchString);
            url.searchParams.append('filters', JSON.stringify(this.filters));
            url.searchParams.append('page', this.currentPage);

            return url.href;
        },
        getDocumentUrl: function (service) {
            return `/claims/${service.claim_id}/documents/${service.document_id}`;
        },
        onDocumentNotes: function (service) {
            eventHub.$emit('show-document-notes', service.client_id, service.claim_id, service.document_id, service.document_type_id, false);
        },
        setChannelNames: function () {

            // clear the array
            this.channels = [];        

            if (this.clientId == 0) {

                // create a channel for each client
                var self = this;
                this.clients.forEach(element => {
                    self.channels.push(`document-tags-event.document.${element.id}`);
                });
            }
            else if (this.clientId > 0) {

                // create a channel for the selected client
                this.channels.push(`document-tags-event.document.${this.clientId}`);
            }
        },        
        listen: function () {

            var self = this;

            // leave all channels
            self.channels.forEach(channel => { window.Echo.leave(channel); });          
            
            // reset the channel names base on the selected client
            self.setChannelNames();               

            // listen on all required channels
            self.channels.forEach(channel => {

                window.Echo.private(channel)
                    .listen('DocumentTagsEvent', (e) => {

                        // check to see if it applies to any of the service documents on the page
                        // no historical service forms here so there will always be a document_id
                        let service = this.services.find(s => s.document_id === e.id);
                        if (service) {
                            this.$set(service, 'tags', e.tags);
                        }                    
                    });
            }); 
        },
        showPayerAuthorization: function(doc) {
            eventHub.$emit('show-link-authorization-modal', doc);
        }                      
    },
    created() {

        if (this.clientIdProperty) {
            this.clientId = this.clientIdProperty;
        }
        else if (this.clients.length === 1) {
            this.clientId = this.clients[0].id;
        }

        this.refresh();
        this.listen();        
    },
    mounted() {        
        this.$refs.searchInput.setAttribute('size', this.$refs.searchInput.getAttribute('placeholder').length);

        eventHub.$off('update-claim-documents');
    }     
}
</script>

<style scoped>
    .h2-italic-sm {
        font-size: .50em !important;
        font-style: italic !important; 
        font-weight: normal !important;    
    }

    .indicator {
        font-size: 1.25em;
        font-weight: bold;
        padding: 0;
        line-height: 1.25rem;
    }

    .badge { 
        position: relative;
        top: -15px;
        left: 7px;
        display: inline-block;
        font-size: 0.75em;
    }

    .small { font-size: 1.2em; }

    td {
        white-space: nowrap;
    }

    .fa-tags {
        font-size: 1.2em;
    }    
</style>